import CantileverButton from 'components/CantileverButton'
import { useRolodexEdit } from 'features/fde'
import { FeatureTemplateItem } from 'features/rightDrawer/drawerStates/crudWorkflows/editRichAssetModel/templates/FeatureTemplateItem'
import { Dispatch, SetStateAction, useCallback, useState } from 'react'
import styled from 'styled-components'
import {
  GeckoAssetModel,
  GeckoComponentModel,
  GeckoFeaturesJSON,
  regenerateAssetModel,
  RolodexEntryJson,
} from 'types'
import { v4 as uuid4 } from 'uuid'

const ComponentTemplateItemContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.625rem;
  border: 1px solid grey;
  border-radius: 0.5rem;
  padding: 0.5rem;
  position: relative;
`

const ComponentNameSection = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
`

const FeaturesList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  padding-left: 1rem;
`

export const ApplyButton = styled(CantileverButton)<{ $isApplied: boolean }>`
  min-height: 1.5rem;
  ${({ $isApplied }) =>
    $isApplied ? 'background-color: rgba(0, 255, 0, 0.3)' : ''};
  &:hover {
    background-color: ${({ $isApplied, theme }) =>
      $isApplied
        ? theme.forecast.redGlassBackground
        : 'rgba(100, 255, 100, 0.2)'} !important;
  }
`

interface ComponentTemplateItemProps {
  templateComponent: GeckoComponentModel
  selectedComponent: GeckoComponentModel
  assetModel: GeckoAssetModel
  setIsLoading: Dispatch<SetStateAction<boolean>>
  forceRefresh: () => Promise<void>
}

export const ComponentTemplateItem = ({
  templateComponent,
  selectedComponent,
  assetModel,
  setIsLoading,
  forceRefresh,
}: ComponentTemplateItemProps) => {
  const { rolodexPatch, rolodexPut } = useRolodexEdit()

  const features = templateComponent.json.features as Record<
    string,
    GeckoFeaturesJSON
  >
  const selectedComponentFeatures = Object.values(
    selectedComponent.json.features as Record<string, GeckoFeaturesJSON>
  ).map((feature) => feature.name)

  const sortedFeatures = Object.values(features).sort((a, b) => {
    if (a?.type === b?.type) {
      return a?.name?.localeCompare(b?.name ?? '') ?? 0
    }
    return a?.type?.localeCompare(b?.type ?? '') ?? 0
  })

  const applyFeatures = useCallback(
    (features: GeckoFeaturesJSON[]) => {
      setIsLoading(true)
      const assetModelRaw = assetModel.raw as RolodexEntryJson
      const assetModelComponents = assetModelRaw.data.components as Record<
        string,
        Record<string, unknown>
      >

      const assetModelComponent = assetModelComponents[
        selectedComponent.id
      ] as Record<string, Record<string, unknown>>

      for (const feature of features) {
        const newFeatureId = uuid4().slice(0, 6)
        assetModelComponent.features[newFeatureId] = feature
      }
      const editedAssetModel = regenerateAssetModel(
        GeckoAssetModel.CreateFromJSON(assetModelRaw)
      )
      rolodexPatch([editedAssetModel]).then(() =>
        forceRefresh().then(() => setIsLoading(false))
      )
    },
    [assetModel, selectedComponent, setIsLoading, rolodexPatch, forceRefresh]
  )

  const removeFeatures = useCallback(
    (features: GeckoFeaturesJSON[]) => {
      setIsLoading(true)
      const assetModelRaw = assetModel.raw as RolodexEntryJson
      const assetModelComponents = assetModelRaw.data.components as Record<
        string,
        Record<string, unknown>
      >
      const assetModelComponent = assetModelComponents[
        selectedComponent.id
      ] as Record<string, Record<string, unknown>>

      for (const feature of features) {
        for (const featureId of Object.keys(assetModelComponent.features)) {
          if (
            (assetModelComponent.features as Record<string, GeckoFeaturesJSON>)[
              featureId
            ]?.name === feature.name
          ) {
            delete assetModelComponent.features[featureId]
          }
        }
      }
      const editedAssetModel = regenerateAssetModel(
        GeckoAssetModel.CreateFromJSON(assetModelRaw)
      )
      rolodexPut([editedAssetModel]).then(() =>
        forceRefresh().then(() => setIsLoading(false))
      )
    },
    [assetModel, selectedComponent, setIsLoading, rolodexPut, forceRefresh]
  )

  const allApplied = Object.values(features).every((feature) =>
    selectedComponentFeatures.includes(feature.name)
  )
  const remainingToApply = Object.values(features).filter(
    (feature) => !selectedComponentFeatures.includes(feature.name)
  )

  const [applyAllHovered, setApplyAllHovered] = useState(false)
  return (
    <ComponentTemplateItemContainer>
      <ComponentNameSection>
        {templateComponent.name}
        <ApplyButton
          style={{ width: '6.5rem' }}
          onClick={() =>
            allApplied
              ? removeFeatures(Object.values(features))
              : applyFeatures(remainingToApply)
          }
          onMouseEnter={() => setApplyAllHovered(true)}
          onMouseLeave={() => setApplyAllHovered(false)}
          $isApplied={allApplied}>
          {allApplied
            ? applyAllHovered
              ? '- Remove All'
              : '✓ All Applied'
            : '+ Apply All'}
        </ApplyButton>
      </ComponentNameSection>
      <FeaturesList>
        {sortedFeatures.map((feature) => (
          <FeatureTemplateItem
            key={feature.name}
            feature={feature}
            selectedComponentFeatures={selectedComponentFeatures}
            removeFeatures={removeFeatures}
            applyFeatures={applyFeatures}
          />
        ))}
      </FeaturesList>
    </ComponentTemplateItemContainer>
  )
}
